/**
 * Full backup/restore (JSON) of financial domain.
 *
 * Versioned dump of user tables from local SQLite. Restore is a
 * MERGE by id (upsert): inserts what doesn't exist and overwrites what does.
 * NEVER deletes local rows that are not present in backup.
 */
import {
  budget,
  category,
  debt,
  debt_payment,
  payee_rule,
  type PowerSyncDrizzleDB,
  project,
  recurring_rule,
  tx,
  tx_template,
  wallet,
  wallet_group,
} from "@cashsync/powersync";
import { eq } from "drizzle-orm";
import { nowISO } from "../../utils/date-helpers";

export const BACKUP_FORMAT_VERSION = 1;

/** Tables included in a full backup, in restoration order. */
export const BACKUP_TABLES = [
  "wallet_group",
  "wallet",
  "category",
  "project",
  "tx",
  "budget",
  "recurring_rule",
  "debt",
  "debt_payment",
  "tx_template",
  "payee_rule",
] as const;

export type BackupTable = (typeof BACKUP_TABLES)[number];

export interface BackupEnvelope {
  version: typeof BACKUP_FORMAT_VERSION;
  exportedAt: string;
  app: "cashsync";
  /** Rows per table as they exist in local SQLite. */
  data: Partial<Record<BackupTable, Record<string, unknown>[]>>;
}

export interface RestoreSummary {
  inserted: Partial<Record<BackupTable, number>>;
  /** Existing rows that were overwritten (merge by id). */
  updated: Partial<Record<BackupTable, number>>;
  /** Invalid rows (missing id or malformed object) ignored. */
  skipped: Partial<Record<BackupTable, number>>;
  errors: string[];
}

/** Superficially validates that a JSON parses to a supported BackupEnvelope. */
export function isBackupEnvelope(value: unknown): value is BackupEnvelope {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const env = value as Partial<BackupEnvelope>;
  if (env.version !== BACKUP_FORMAT_VERSION || env.app !== "cashsync") {
    return false;
  }
  if (typeof env.data !== "object" || env.data === null || Array.isArray(env.data)) {
    return false;
  }
  return Object.values(env.data).every((rows) => Array.isArray(rows));
}

/** Builds versioned envelope (pure; `exportedAt` injectable for tests). */
export function buildBackupEnvelope(
  data: BackupEnvelope["data"],
  exportedAt: string = nowISO(),
): BackupEnvelope {
  return { version: BACKUP_FORMAT_VERSION, exportedAt, app: "cashsync", data };
}

const JSON_INDENT = 2;

/** Serializes backup to readable JSON (ready to download as .json). */
export function serializeBackup(envelope: BackupEnvelope): string {
  return JSON.stringify(envelope, null, JSON_INDENT);
}

/** Parses and validates a backup .json. Returns null if not a valid backup. */
export function parseBackupJson(json: string): BackupEnvelope | null {
  try {
    const parsed: unknown = JSON.parse(json);
    return isBackupEnvelope(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

type BackupRow = Record<string, unknown>;

function isBackupRow(value: unknown): value is BackupRow {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export interface RestoreRowSplit {
  toInsert: BackupRow[];
  toUpdate: BackupRow[];
  invalid: number;
}

/**
 * Decides, row by row, whether to insert or update (merge by id).
 * Pure and testable; rows without string `id` are discarded.
 */
export function splitRestoreRows(
  rows: readonly unknown[],
  existingIds: ReadonlySet<string>,
): RestoreRowSplit {
  const split: RestoreRowSplit = { toInsert: [], toUpdate: [], invalid: 0 };
  for (const row of rows) {
    if (!isBackupRow(row)) {
      split.invalid += 1;
      continue;
    }
    const candidate = row;
    if (typeof candidate.id !== "string" || !candidate.id) {
      split.invalid += 1;
      continue;
    }
    if (existingIds.has(candidate.id)) {
      split.toUpdate.push(candidate);
    } else {
      split.toInsert.push(candidate);
    }
  }
  return split;
}

// ─── Table access ────────────────────────────────────────────────────────────

// Deliberately loose typing: all 11 tables share the shape (id + user_id) but
// their Drizzle models differ. Backup works with raw rows
// (Record<string, unknown>) and casts them at single insertion point.
const TABLE_MAP = {
  wallet_group,
  wallet,
  category,
  project,
  tx,
  budget,
  recurring_rule,
  debt,
  debt_payment,
  tx_template,
  payee_rule,
} as const;

/**
 * Complete dump of user tables. Rows come out as they are in
 * local SQLite (snake_case, amounts in minor units).
 */
export async function exportBackupData(
  db: PowerSyncDrizzleDB,
  userId: string,
): Promise<BackupEnvelope> {
  const data: BackupEnvelope["data"] = {};
  for (const name of BACKUP_TABLES) {
    const table = TABLE_MAP[name];
    const rows = await db.select().from(table).where(eq(table.user_id, userId));
    data[name] = rows;
  }
  return buildBackupEnvelope(data);
}

/**
 * Restores a backup in merge by id mode (upsert):
 *   - new rows → INSERT (with `user_id` forced to current user);
 *   - existing rows → full UPDATE;
 *   - DOES NOT delete anything not in backup.
 *
 * All inside a local transaction; PowerSync uploads changes afterwards.
 *
 * Deliberately DOES NOT go through `patchRowVerified` (see
 * `../../powersync/verified-write.ts`): unlike other mutations,
 * here the `UPDATE` of each row in `split.toUpdate` is preceded, WITHIN THE
 * SAME transaction, by the read (`trx.select({ id })`) that decided that row
 * already existed — there is no window between "decided to exist" and "is
 * updated", both happen in the same `trx` without yielding to a
 * view reconstruction. Signal that would reopen it: a restore whose summary
 * counts a row as updated but the restored value does not land.
 */
export async function restoreBackup(
  db: PowerSyncDrizzleDB,
  userId: string,
  envelope: BackupEnvelope,
): Promise<RestoreSummary> {
  const summary: RestoreSummary = { inserted: {}, updated: {}, skipped: {}, errors: [] };

  await db.transaction(async (trx) => {
    for (const name of BACKUP_TABLES) {
      const rows = envelope.data[name];
      if (!rows || rows.length === 0) {
        continue;
      }
      const table = TABLE_MAP[name];
      try {
        const existing = await trx
          .select({ id: table.id })
          .from(table)
          .where(eq(table.user_id, userId));
        const existingIds = new Set(existing.map((r) => r.id));
        const split = splitRestoreRows(rows, existingIds);

        for (const row of split.toInsert) {
          const values = { ...row, user_id: userId };
          // Trust-boundary: row comes from user's own backup already validated as
          // object with id; drizzle/SQLite ignore invalid keys with error.
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          await trx.insert(table).values(values as typeof table.$inferInsert);
        }
        for (const row of split.toUpdate) {
          const { id, ...rest } = row;
          const values = { ...rest, user_id: userId };
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          await trx
            .update(table)
            .set(values as Partial<typeof table.$inferInsert>)
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            .where(eq(table.id, id as string));
        }

        summary.inserted[name] = split.toInsert.length;
        summary.updated[name] = split.toUpdate.length;
        if (split.invalid > 0) {
          summary.skipped[name] = split.invalid;
        }
      } catch (error) {
        summary.errors.push(`${name}: ${error instanceof Error ? error.message : String(error)}`);
        throw error; // aborts the transaction: a partial restore would be worse
      }
    }
  });

  return summary;
}
